import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ContactListType } from '@citrite/sf-api';
import { Close } from '@sharefiledev/icons';
import { Select, Spin, Tag } from 'antd';
import { debounce } from 'lodash';
import { COLORS, REMOVE_TAG_BUTTON_CLASS } from '../../constants/constants';
import { ContactOption } from '../../layouts/Fields/Edit/ContactOption';
import { FieldProperties, InputObj, TagRender } from '../../layouts/Layouts';
import { StyledDropdown } from '../../layouts/table/DataTable.styled';
import { UserDetails } from '../../model/types';
import {
	AssigneeMultiUserFieldWrapper,
	StyledSpanVerticalAlign,
	StyledTagRemoveBtn,
} from '../../schema-components/antd/edit/ContactMultiSelect/ContactMultiSelect.styled';
import { t } from '../../utils';
import { useContactSearch } from '../../utils/hooks/useContactSearch/useContactSearch';

export type ContactOptionType = {
	label: ReactNode;
	value: string;
	details: any;
};
export interface ContactOptionProps {
	userType: string;
	firstName: string;
	lastName: string;
	email: string;
	id: string;
	roleMapper?: Record<string, string>;
}
export interface ContactMultiSelectProps extends FieldProperties {
	validations?: {
		type: [string];
		limit?: {
			value: number;
			message: string;
		};
	};
	attributes?: {
		userGroup?: string;
		allowMultiSelect?: boolean;
	};
	getUsers: (ids: string[]) => Promise<UserDetails[]>;
	isFilter?: boolean;
	userList?: Record<string, any>;
	roleMapper?: Record<string, any>;
}

export const ContactMultiSelect: React.FC<ContactMultiSelectProps> = ({
	value,
	onChange,
	attributes,
	label,
	disabled,
	forwardedRef,
	className,
	dataTestId,
	userList: users,
	roleMapper,
	getUsers,
}) => {
	const [term, setTerm] = React.useState('');
	const [contactsList, setContactsList] = React.useState<ContactOptionType[]>([]);
	const [selectedContacts, setSelectedContacts] = React.useState<ContactOptionType[]>([]);
	const [loading, setIsLoading] = useState(true);
	const [initialContactsList, setInitialContactsList] = useState<ContactOptionProps[]>(
		[]
	);

	const { allowMultiSelect = true, userGroup = ContactListType.AllUsers } =
		attributes || {
			allowMultiSelect: true,
			userGroup: ContactListType.AllUsers,
		};

	const { searchContacts } = useContactSearch();

	const fetchUsers = useCallback(async () => {
		const isFieldEmpty = () => !value || (Array.isArray(value) && value.length === 0);
		const userList: Record<string, any> = users || [];
		let _users: ContactOptionProps[];
		if (attributes && attributes.userGroup) {
			_users = initialContactsList.length
				? initialContactsList
				: (await searchContacts('', userGroup ?? ContactListType.AllUsers)) ?? [];
		} else {
			_users = Object.keys(userList).map(key => {
				return {
					firstName: userList[key].FirstName || '',
					lastName: userList[key].LastName || '',
					email: userList[key].Email,
					id: userList[key].Id,
					userType: userList[key].userType,
					roleMapper,
				};
			});
		}
		setInitialContactsList(_users);
		setContactsList(_users.map(convertToOption));
		isFieldEmpty() && setIsLoading(false);
		return;
	}, [
		attributes,
		initialContactsList,
		userGroup,
		roleMapper,
		searchContacts,
		users,
		value,
	]);

	useEffect(() => {
		fetchUsers();
	}, [fetchUsers]);

	/**
	 * This hook is using for reset the values
	 * when user click clear in filter section
	 */
	useEffect(() => {
		if (!value || (value && Array.isArray(value) && value.length === 0)) {
			setContactsList(initialContactsList.map(convertToOption));
			setSelectedContacts([]);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	const loadAssignees = useCallback(async (ids: string[]) => {
		const contactOptions: ContactOptionType[] = (await getUsers(ids)).map(user => {
			return convertToOption({
				firstName: user.FirstName || '',
				lastName: user.LastName || '',
				email: user.Email,
				id: user.Id,
				userType: user.userType,
				roleMapper,
			});
		});
		setIsLoading(false);
		setSelectedContacts(contactOptions);
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (value && Array.isArray(value) && value.length > 0) {
			loadAssignees(value);
		} else {
			setContactsList([]);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadAssignees]);
	useEffect(() => {
		if (term.length === 0) {
			setContactsList(initialContactsList.map(convertToOption));
		} else {
			userFind(term);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [term, initialContactsList]);

	const debouncedSearch = useMemo(
		() =>
			debounce(debouncedValue => {
				setTerm(debouncedValue);
			}, 500),

		[setTerm]
	);

	const userFind = (filterTerm: string) => {
		const contactOptions = initialContactsList
			.filter(
				(contact: ContactOptionProps) =>
					contact.firstName.toLowerCase().includes(filterTerm.toLowerCase()) ||
					contact.lastName.toLowerCase().includes(filterTerm.toLowerCase()) ||
					contact.email.toLowerCase().includes(filterTerm.toLowerCase())
			)
			.map(convertToOption);
		setContactsList(contactOptions);
		return contactOptions;
	};

	const SelectedUserLabel: React.FC<any> = ({ firstName, lastName, email }) => (
		<>{firstName || lastName ? `${firstName} ${lastName}` : email ?? ''}</>
	);

	const renderTag: TagRender = ({
		label: tagLabel,
		value: tagValue,
		closable,
		onClose,
	}) => {
		const contact = selectedContacts?.find(
			(option: ContactOptionType) => option.value === tagValue
		)?.details;

		return (
			<Tag
				closeIcon={
					<StyledTagRemoveBtn
						className={REMOVE_TAG_BUTTON_CLASS}
						aria-label={t(
							'dynamic-components:manageField.fields.userMultiSelect.removeBtnAriaLabel',
							{
								userName: contact ? contact.email : '',
							}
						)}
						onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
							if (e.key === 'Enter') {
								e.stopPropagation();
								onClose();
							}
						}}
						type="button"
					>
						<StyledSpanVerticalAlign>
							<Close size={16} />
						</StyledSpanVerticalAlign>
					</StyledTagRemoveBtn>
				}
				bordered={false}
				style={{
					marginInlineEnd: 4,
					background: COLORS['default'].background,
					color: COLORS['default'].color,
				}}
				closable={closable}
				onClose={onClose}
			>
				{contact ? <SelectedUserLabel {...contact} /> : tagLabel}
			</Tag>
		);
	};

	const updateList = (contact: any) => {
		const data: InputObj = { value: [], errorMessage: '' };
		setSelectedContacts(prevList => {
			// Check if the new object already exists in the list
			const index = prevList.findIndex(
				(item: ContactOptionType) => item.value === contact.value
			);

			let updatedList: ContactOptionType[] = [];
			if (index !== -1) {
				// If it exists, remove it from the list
				updatedList = prevList.filter((_, i) => i !== index);
			} else {
				// If it doesn't exist, add the new object to the list
				updatedList = [...prevList, contact];
			}
			const userIds = updatedList.map((e: ContactOptionType) => e.value);
			// Call the callback function with the updated list
			if (onChange) {
				onChange({ ...data, value: userIds });
			}
			return updatedList;
		});
	};
	const renderDropdown = (menu: any) => {
		return loading ? (
			<div>
				<Spin size="small" />
			</div>
		) : (
			<StyledDropdown>{menu}</StyledDropdown>
		);
	};

	return (
		<AssigneeMultiUserFieldWrapper isEditable={!disabled}>
			<Select
				mode="multiple"
				autoFocus
				data-testid={dataTestId}
				labelInValue
				filterOption={false}
				placeholder={t('dynamic-components:userSelect.search')}
				onSearch={debouncedSearch}
				notFoundContent={loading ? <Spin size="small" /> : null}
				style={{ width: '100%' }}
				showSearch
				maxTagCount="responsive"
				onSelect={(_, option) => updateList(option)}
				onDeselect={updateList}
				value={selectedContacts?.map((option: ContactOptionType) => ({
					label: <SelectedUserLabel {...option.details} />,
					value: option.details.id!,
				}))}
				ref={forwardedRef}
				aria-label={`${label} ${allowMultiSelect ? 'multi-select' : 'select'}`}
				options={contactsList}
				dropdownRender={renderDropdown}
				tagRender={renderTag}
				className={className}
			/>
		</AssigneeMultiUserFieldWrapper>
	);
};

export const convertToOption = (contact: ContactOptionProps): ContactOptionType => {
	return {
		label: <ContactOption {...contact} />,
		value: contact.id!,
		details: contact,
	};
};
